// src/components/NavBar.jsx
import React, { useEffect, useContext, useState } from 'react';
import { AppBar, Toolbar, Typography, Button, Box } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';
import WebhookHelpModal from './WebhookHelpModal';

export default function NavBar() {
  // contexto + rotas
  const auth = useContext(AuthContext);
  const { userTipo, username } = auth || {};
  const navigate = useNavigate();
  const location = useLocation();

  const [webhookOpen, setWebhookOpen] = useState(false);

  // tenta achar o código do representante
  const repCode =
    auth?.chave_representante ||
    auth?.codigo_representante ||
    auth?.codigo ||
    localStorage.getItem('chave_representante') ||
    localStorage.getItem('codigo_representante') ||
    localStorage.getItem('chave_usuario') ||
    localStorage.getItem('chave') ||
    '';

  // Favicon 🔑
  useEffect(() => {
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
        <text y=".9em" font-size="90">🔑</text>
      </svg>`;
    const href = 'data:image/svg+xml,' + encodeURIComponent(svg);

    let link = document.querySelector('link[rel="icon"][data-dynamic-favicon]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'icon');
      link.setAttribute('data-dynamic-favicon', '1');
      document.head.appendChild(link);
    }
    link.type = 'image/svg+xml';
    link.href = href;

    if (!document.title) document.title = 'Painel de Licenças';
  }, []);

  // Buscar links de admin (downloads/suporte)
  useEffect(() => {
    if (location.pathname === '/login') return;

    (async () => {
      try {
        const { data } = await api.get('/usuarios/admin-links');
        localStorage.setItem('admin_link_drive_downloads', data.link_drive_downloads || '');
        localStorage.setItem('admin_link_whatsapp', data.link_whatsapp || '');
      } catch (err) {
        console.error('Erro ao buscar links do admin:', err);
      }
    })();
  }, [location.pathname]);

  // Esconde a barra na tela de login
  if (location.pathname === '/login') return null;

  const stickyAppBarSx = {
    position: 'sticky',
    top: 0,
    zIndex: (t) => t.zIndex.appBar + 1,
    borderBottom: 1,
    borderColor: 'divider',
    backdropFilter: 'saturate(180%) blur(6px)',
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const handleDownloads = async () => {
    try {
      const { data } = await api.get('/usuarios/admin-links');
      const link = data.link_drive_downloads || localStorage.getItem('admin_link_drive_downloads') || '';
      if (link) window.open(link, '_blank');
      else alert('⚠️ ERRO: link de downloads NÃO configurado.');
    } catch (err) {
      console.error('ERRO ao buscar link de downloads:', err);
      alert('⚠️ ERRO ao buscar link de downloads.');
    }
  };

  const handleSuporte = async () => {
    try {
      const { data } = await api.get('/usuarios/admin-links');
      const numero = data.link_whatsapp || localStorage.getItem('admin_link_whatsapp') || '';
      if (numero) window.open(`https://wa.me/${numero}`, '_blank');
      else alert('⚠️ ERRO: link do WhatsApp NÃO configurado.');
    } catch (err) {
      console.error('ERRO ao buscar link do WhatsApp:', err);
      alert('⚠️ ERRO ao buscar link do WhatsApp.');
    }
  };

  const isActive = (path) => location.pathname.startsWith(path);

  // Barra para "licenca" (cliente final)
  if (userTipo === 'licenca') {
    return (
      <AppBar sx={stickyAppBarSx}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            FlowEditor
          </Typography>
          <Typography variant="body1" sx={{ mr: 2 }}>
            {(username || '').toUpperCase()}
          </Typography>
          <Button color="inherit" onClick={handleLogout}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>
    );
  }

  // Barra para admin/revendedor
  return (
    <AppBar sx={stickyAppBarSx}>
      <Toolbar>
        <Typography
          variant="h6"
          component="div"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/dashboard')}
        >
          Painel de Licenças
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {userTipo === 'admin' && (
            <Button color="inherit" onClick={() => navigate('/acessos')}>
              Logs Acessos
            </Button>
          )}
          {userTipo === 'admin' && (
            <Button color="inherit" onClick={() => navigate('/relatorios/excluidas')}>
              Logs Ativações Excluídas
            </Button>
          )}
          {userTipo === 'admin' && (
            <Button color="inherit" onClick={() => navigate('/usuarios')}>
              Usuários
            </Button>
          )}

          <Button
            color="inherit"
            variant={isActive('/licencas') ? 'outlined' : 'text'}
            onClick={() => navigate('/licencas')}
          >
            Licenças
          </Button>

          {(userTipo === 'admin' || userTipo === 'revendedor') && (
            <Button
              color="inherit"
              variant={isActive('/kanban') ? 'outlined' : 'text'}
              onClick={() => navigate('/kanban')}
            >
              Kanban
            </Button>
          )}

          <Button color="inherit" onClick={handleDownloads}>
            Downloads
          </Button>
          <Button color="inherit" onClick={handleSuporte}>
            Suporte
          </Button>

          {(userTipo === 'admin' || userTipo === 'revendedor') && (
            <Button color="inherit" onClick={() => setWebhookOpen(true)}>
              Webhook
            </Button>
          )}

          <Button color="inherit" onClick={() => navigate('/whitelabel')}>
            White Label
          </Button>

          {(userTipo === 'admin' || userTipo === 'revendedor') && (
            <Button color="inherit" onClick={() => navigate('/tokens')}>
              Tokens
            </Button>
          )}

          <Button color="inherit" onClick={handleLogout}>
            Logout
          </Button>
        </Box>
      </Toolbar>

      <WebhookHelpModal
        open={webhookOpen}
        onClose={() => setWebhookOpen(false)}
        repCode={repCode}
      />
    </AppBar>
  );
}
